/**
 * Tipos para Sistema de Logs de Auditoria (Epic 38 - Story 38.1)
 *
 * Sistema de auditoria automática que registra todas operações CRUD.
 * Features:
 * - Multi-column sorting (até 3 níveis com Shift+Click)
 * - Filtros avançados (operação, entidade, usuário, data)
 * - Export XLSX com formatação profissional
 * - JSON diff para visualizar alterações
 */

// ============================================================================
// TIPOS BASE
// ============================================================================

export type OperationType = 'CREATE' | 'UPDATE' | 'DELETE' | 'UPLOAD' | 'IMPORT' | 'SYNC' | 'FINALIZE' | 'RESTORE' | 'OTHER' | 'ERROR';

export interface AuditLog {
  id: number;
  usuario_nome: string | null;
  usuario_email?: string | null;
  empresa: string | null;
  operacao: OperationType;
  operacao_display: string;
  entidade: string;
  id_entidade: string;
  ip: string | null;
  user_agent: string | null;
  valores_anteriores: Record<string, any> | null;
  valores_novos: Record<string, any> | null;
  data_hora: string; // ISO datetime
}

export interface AuditLogListItem {
  id: number;
  usuario_nome: string | null;
  empresa: string | null;
  operacao: OperationType;
  operacao_display: string;
  entidade: string;
  id_entidade: string;
  ip: string | null;
  data_hora: string; // ISO datetime
}

// ============================================================================
// FILTROS
// ============================================================================

export interface AuditLogFilters {
  operacao?: OperationType[];
  entidade?: string[];
  usuario_search?: string;
  empresa?: string;
  data_inicio?: string; // ISO datetime
  data_fim?: string; // ISO datetime
  origem?: 'WEB' | 'MOBILE' | ''; // S27.3 - Filter by source (web/mobile)
}

// ============================================================================
// SORTING (Multi-column)
// ============================================================================

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

/**
 * Multi-column sort configuration (até 3 níveis)
 * Exemplo: [
 *   { key: 'data_hora', direction: 'desc' },  // Priority ①
 *   { key: 'operacao', direction: 'asc' },    // Priority ②
 *   { key: 'entidade', direction: 'asc' }     // Priority ③
 * ]
 */
export type MultiSortConfig = SortConfig[];

// ============================================================================
// PAGINAÇÃO
// ============================================================================

export interface AuditLogPaginatedResponse {
  results: AuditLogListItem[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ============================================================================
// OPERAÇÕES E ENTIDADES
// ============================================================================

export interface OperationOption {
  value: OperationType;
  label: string;
}

export interface EntityCount {
  entidade: string;
  count: number;
}

// ============================================================================
// JSON DIFF
// ============================================================================

export interface JsonDiffField {
  field: string;
  before: any;
  after: any;
  changed: boolean;
}

// ============================================================================
// BADGE CONFIGURATION
// ============================================================================

export interface OperationBadgeConfig {
  color: string; // Tailwind color class
  bgColor: string; // Tailwind bg color class
  text: string; // Display text
  icon?: string; // Optional icon name (lucide-react)
}

export const OPERATION_BADGE_CONFIG: Record<OperationType, OperationBadgeConfig> = {
  CREATE: {
    color: 'text-green-800 dark:text-green-200',
    bgColor: 'bg-green-100 dark:bg-green-900',
    text: 'CRIAÇÃO',
    icon: 'Plus',
  },
  UPDATE: {
    color: 'text-blue-800 dark:text-blue-200',
    bgColor: 'bg-blue-100 dark:bg-blue-900',
    text: 'ATUALIZAÇÃO',
    icon: 'Edit',
  },
  DELETE: {
    color: 'text-red-800 dark:text-red-200',
    bgColor: 'bg-red-100 dark:bg-red-900',
    text: 'EXCLUSÃO',
    icon: 'Trash2',
  },
  UPLOAD: {
    color: 'text-yellow-800 dark:text-yellow-200',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900',
    text: 'UPLOAD',
    icon: 'Upload',
  },
  IMPORT: {
    color: 'text-purple-800 dark:text-purple-200',
    bgColor: 'bg-purple-100 dark:bg-purple-900',
    text: 'IMPORTAÇÃO',
    icon: 'FileDown',
  },
  SYNC: {
    color: 'text-cyan-800 dark:text-cyan-200',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900',
    text: 'SINCRONIZAÇÃO',
    icon: 'RefreshCw',
  },
  FINALIZE: {
    color: 'text-emerald-800 dark:text-emerald-200',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900',
    text: 'FINALIZAÇÃO',
    icon: 'CheckCircle',
  },
  // N217: restaurar contagem da lixeira (antes caia no DESCONHECIDO)
  RESTORE: {
    color: 'text-teal-800 dark:text-teal-200',
    bgColor: 'bg-teal-100 dark:bg-teal-900',
    text: 'RESTAURAÇÃO',
    icon: 'RotateCcw',
  },
  OTHER: {
    color: 'text-gray-800 dark:text-gray-200',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    text: 'OUTRO',
    icon: 'MoreHorizontal',
  },
  ERROR: {
    color: 'text-orange-800 dark:text-orange-200',
    bgColor: 'bg-orange-100 dark:bg-orange-900',
    text: 'ERRO',
    icon: 'AlertTriangle',
  },
};

// ============================================================================
// EXPORT XLSX
// ============================================================================

export interface ExportXlsxParams extends AuditLogFilters {
  sort?: string; // Multi-column sort string: "col1:asc,col2:desc,col3:asc"
  limit?: number; // Max records (default 1000, max 10000)
}
